import { defineComponent as _defineComponent } from 'vue';
import { unref as _unref, createElementVNode as _createElementVNode, withCtx as _withCtx, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createCommentVNode as _createCommentVNode, createBlock as _createBlock, resolveDirective as _resolveDirective, withDirectives as _withDirectives, createSlots as _createSlots, normalizeClass as _normalizeClass } from "vue";
const _hoisted_1 = {
    key: 0,
    class: "flex items-center px-6 py-2 text-sm text-gray-100 h-14 bg-desaturated-primary-700 dark:bg-gray-850"
};
const _hoisted_2 = ["src"];
const _hoisted_3 = { class: "truncate max-w-44" };
const _hoisted_4 = {
    key: 0,
    class: "flex items-center justify-center ml-4"
};
const _hoisted_5 = { class: "truncate max-w-44" };
const _hoisted_6 = { class: "flex items-center justify-center ml-4" };
const _hoisted_7 = { class: "flex flex-col w-52" };
import { computed, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import { storeToRefs } from 'pinia';
import { useRoute, useRouter, RouterLink } from 'vue-router';
import componentI18n from '@/i18n/componentI18n';
import DropdownMenu from '@/components/DropdownMenu.vue';
import DropdownMenuItem from '@/components/DropdownMenuItem.vue';
import SvgIcon from './SvgIcon.vue';
import { isWorkspace } from '@/types/workspaces';
import { getWorkspaces } from '@/data/workspaces';
import { useRetroStore } from '@/pinia_stores/retro';
export default /*#__PURE__*/ _defineComponent({
    __name: 'Breadcrumb',
    setup(__props) {
        const store = useStore();
        const route = useRoute();
        const router = useRouter();
        const retroStore = useRetroStore();
        const { retroName } = storeToRefs(retroStore);
        const isDropdownOpen = ref(false);
        const isDarkMode = computed(() => store.getters.isDarkMode);
        const workspace = computed(() => store.getters.workspace);
        const team = computed(() => store.getters.team);
        const avatar = computed(() => {
            return workspace.value?.avatar || require('@/assets/images/default-workspace-avatar.png');
        });
        const workspaces = ref(null);
        onMounted(async () => {
            if (route.name === 'showWorkspace') {
                try {
                    workspaces.value = await getWorkspaces();
                }
                catch (e) {
                    store.dispatch('flashThenClear', { message: 'error_loading_workspaces' });
                }
            }
        });
        const isWorkspaceOwner = computed(() => store.getters.isWorkspaceOwner);
        const isWorkspaceSuspended = computed(() => isWorkspace(workspace.value) && workspace.value.isSuspended);
        const locale = computed(() => store.getters.locale);
        const getTranslationFor = (key) => {
            const obj = componentI18n('components.breadcrumb').i18n.messages[locale.value];
            const parts = key.split('.');
            return parts.reduce((acc, p) => acc[p], obj);
        };
        const joinedWorkspacesWithoutCurrent = computed(() => {
            const result = workspaces.value
                ?.filter((w) => !w.invitationId)
                .filter((w) => w.id !== workspace.value?.id)
                .sort((w1, w2) => {
                const t1 = new Date(w1.lastVisitedAt).getTime();
                const t2 = new Date(w2.lastVisitedAt).getTime();
                return t2 - t1;
            });
            return result || [];
        });
        const userHasPendingWorkspacesInvites = computed(() => workspaces.value?.some((w) => w.invitationId) || false);
        const maxWorkspacesItems = 4;
        const switchWorkspaceSubItems = computed(() => {
            const items = joinedWorkspacesWithoutCurrent.value.map((w) => {
                return {
                    icon: w.avatar || require('@/assets/images/default-workspace-avatar.png'),
                    key: w.id,
                    text: w.name,
                    action: () => router.push({
                        name: 'showWorkspace',
                        params: { workspaceId: w.id }
                    })
                };
            });
            if (items.length > maxWorkspacesItems || userHasPendingWorkspacesInvites.value) {
                items.splice(maxWorkspacesItems);
                items.push({
                    icon: 'switch',
                    key: 'switch',
                    text: getTranslationFor('workspace_submenu.choose_another'),
                    action: () => router.push({ name: 'indexWorkspace' })
                });
            }
            return items || [];
        });
        const showPricingReleaseDashboard = computed(() => store.getters.showPricingReleaseDashboard);
        const workspaceSubmenu = computed(() => {
            const submenu = [
                {
                    icon: 'new',
                    text: getTranslationFor('workspace_submenu.create'),
                    action: () => router.push({ name: 'indexWorkspace' })
                }
            ];
            if (!isWorkspaceSuspended.value && !showPricingReleaseDashboard.value) {
                submenu.unshift({
                    icon: 'archive',
                    text: getTranslationFor('workspace_submenu.archived_teams'),
                    action: () => router.push({ name: 'archivedTeams' })
                });
            }
            if (!isWorkspaceSuspended.value &&
                (!showPricingReleaseDashboard.value ||
                    (showPricingReleaseDashboard.value && isWorkspaceOwner.value))) {
                submenu.unshift({
                    icon: 'users',
                    text: getTranslationFor('workspace_submenu.users'),
                    action: () => router.push({
                        name: 'membersWorkspace',
                        params: { workspaceId: workspace.value.id }
                    })
                });
            }
            if (isWorkspaceOwner.value && !showPricingReleaseDashboard.value) {
                submenu.unshift({
                    icon: 'gear',
                    text: getTranslationFor('workspace_submenu.settings'),
                    action: () => router.push({
                        name: 'editWorkspace',
                        params: { workspaceId: workspace.value.id }
                    })
                });
            }
            if (switchWorkspaceSubItems.value.length) {
                submenu.push({
                    icon: 'switch',
                    text: getTranslationFor('workspace_submenu.switch'),
                    submenuItems: switchWorkspaceSubItems.value
                });
            }
            return submenu;
        });
        const teamSubmenu = computed(() => {
            return [
                {
                    icon: 'write',
                    text: getTranslationFor('team_submenu.edit'),
                    action: () => router.push({
                        name: 'editTeam',
                        params: { workspaceId: workspace.value.id, teamId: team.value.id }
                    })
                }
            ];
        });
        const fragments = computed(() => {
            return route.path
                .split('/')
                .map((p) => {
                // Workspace show
                if (p === 'workspaces' && workspace.value) {
                    return {
                        displayText: workspace.value.name,
                        navigationUrl: `/workspaces/${workspace.value.id}`,
                        submenu: workspaceSubmenu.value
                    };
                }
                if (p === 'users' && workspace.value) {
                    return {
                        displayText: getTranslationFor('workspace_members'),
                        navigationUrl: route.fullPath // Final route
                    };
                }
                if (p === 'archived_teams' && workspace.value) {
                    return {
                        displayText: getTranslationFor('archived_teams'),
                        navigationUrl: route.fullPath // Final route
                    };
                }
                // Team show
                if (p === 'teams' && team.value) {
                    return {
                        displayText: team.value.name,
                        navigationUrl: `/workspaces/${workspace.value.id}/teams/${team.value.id}`,
                        submenu: teamSubmenu.value
                    };
                }
                if (p === 'edit' && workspace.value && !team.value) {
                    return {
                        displayText: getTranslationFor('edit_workspace'),
                        navigationUrl: `/workspaces/${workspace.value.id}/edit`
                    };
                }
                if (p === 'delete' && workspace.value && !team.value) {
                    return {
                        displayText: getTranslationFor('delete_workspace'),
                        navigationUrl: `/workspaces/${workspace.value.id}/edit/delete`
                    };
                }
                if (p === 'edit' && team.value) {
                    return {
                        displayText: getTranslationFor('edit_team'),
                        navigationUrl: route.fullPath
                    };
                }
                // Retro show (final url, for now)
                if (p === 'retros' && retroName.value) {
                    return {
                        displayText: retroName.value,
                        navigationUrl: route.fullPath
                    };
                }
                // Team new (final url)
                if (p === 'new' && route.fullPath.includes('/teams/new')) {
                    return {
                        displayText: getTranslationFor('create_team'),
                        navigationUrl: route.fullPath
                    };
                }
                // Retro new (final url)
                if (p === 'new' && route.fullPath.includes('/retros/new')) {
                    return {
                        displayText: getTranslationFor('create_retro'),
                        navigationUrl: route.fullPath
                    };
                }
                if (p === 'plan' && workspace.value) {
                    return {
                        displayText: getTranslationFor('workspace_change_plan'),
                        navigationUrl: `/workspaces/${workspace.value.id}/edit/plan`
                    };
                }
                if (p === 'seats' && workspace.value) {
                    return {
                        displayText: getTranslationFor('workspace_seats'),
                        navigationUrl: `/workspaces/${workspace.value.id}/edit/seats`
                    };
                }
                return null;
            })
                .filter((f) => f);
        });
        function triggerDropdown() {
            isDropdownOpen.value = !isDropdownOpen.value;
        }
        function isLastFragment(index) {
            return index === fragments.value.length - 1;
        }
        function isFirstFragment(index) {
            return index === 0;
        }
        return (_ctx, _cache) => {
            const _directive_close_popover = _resolveDirective("close-popover");
            return (_unref(workspace))
                ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
                    _createVNode(_unref(RouterLink), {
                        to: { name: 'showWorkspace', params: { workspaceId: _unref(workspace).id } }
                    }, {
                        default: _withCtx(() => [
                            _createElementVNode("img", {
                                class: "w-10 h-10 mr-6 border border-white border-solid rounded-md",
                                src: _unref(avatar),
                                alt: "Workspace avatar"
                            }, null, 8, _hoisted_2)
                        ], undefined, true),
                        _: 1
                    }, 8, ["to"]),
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(fragments), (f, i) => {
                        return (_openBlock(), _createElementBlock("div", {
                            key: f.navigationUrl,
                            class: _normalizeClass(["flex min-w-0 rounded-md hover:bg-opacity-10", {
                                    'hover:bg-white': _unref(isDarkMode),
                                    'hover:bg-black': !_unref(isDarkMode),
                                    'bg-opacity-10 bg-black dark:bg-opacity-10 dark:bg-white': isLastFragment(i) && isDropdownOpen.value
                                }])
                        }, [
                            (!f.submenu || !isLastFragment(i))
                                ? (_openBlock(), _createBlock(_unref(RouterLink), {
                                    key: 0,
                                    to: _unref(showPricingReleaseDashboard) && !isFirstFragment(i) ? _unref(route).fullPath : f.navigationUrl,
                                    class: "flex items-center min-w-0 p-2"
                                }, {
                                    default: _withCtx(() => [
                                        _createElementVNode("div", _hoisted_3, _toDisplayString(f.displayText), 1),
                                        (!isLastFragment(i))
                                            ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                                                _createVNode(SvgIcon, {
                                                    class: "w-2 h-2 transform rotate-90",
                                                    name: "arrow-up"
                                                })
                                            ]))
                                            : _createCommentVNode("", true)
                                    ], undefined, true),
                                    _: 2
                                }, 1032, ["to"]))
                                : (_openBlock(), _createBlock(DropdownMenu, {
                                    key: 1,
                                    open: isDropdownOpen.value,
                                    "onUpdate:open": _cache[0] || (_cache[0] = ($event) => ((isDropdownOpen).value = $event)),
                                    ident: ""
                                }, {
                                    item: _withCtx(() => [
                                        _createElementVNode("button", {
                                            class: "flex items-center min-w-0 p-2",
                                            onClick: triggerDropdown
                                        }, [
                                            _createElementVNode("div", _hoisted_5, _toDisplayString(f.displayText), 1),
                                            _createElementVNode("div", _hoisted_6, [
                                                _createVNode(SvgIcon, {
                                                    class: "w-2 h-2 transform rotate-180",
                                                    name: "arrow-up"
                                                })
                                            ])
                                        ])
                                    ]),
                                    "menu-items": _withCtx(() => [
                                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(f.submenu, (s1) => {
                                            return (_openBlock(), _createBlock(DropdownMenuItem, {
                                                key: s1.key || s1.text,
                                                text: s1.text,
                                                icon: s1.icon,
                                                onClick: s1.action
                                            }, _createSlots({ _: 2 }, [
                                                (s1.submenuItems)
                                                    ? {
                                                        name: "submenu",
                                                        fn: _withCtx(() => [
                                                            _createElementVNode("div", _hoisted_7, [
                                                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(s1.submenuItems, (s2) => {
                                                                    return _withDirectives((_openBlock(), _createBlock(DropdownMenuItem, {
                                                                        key: s2.key || s2.text,
                                                                        text: s2.text,
                                                                        icon: s2.icon,
                                                                        onClick: s2.action
                                                                    }, null, 8, ["text", "icon", "onClick"])), [
                                                                        [_directive_close_popover]
                                                                    ]);
                                                                }), 128))
                                                            ])
                                                        ])
                                                    }
                                                    : undefined
                                            ]), 1032, ["text", "icon", "onClick"]));
                                        }), 128))
                                    ]),
                                    _: 2
                                }, 1032, ["open"]))
                        ], 2));
                    }), 128))
                ]))
                : _createCommentVNode("", true);
        };
    }
});
